import { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Button,
  Grid,
  InputAdornment,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@mui/material'
import useCustomStyles from '../../../utils/hooks/CustomStylesHook'
import { useParams } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { AppDispatch } from '../../../app/store'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { useAppSelector } from '../../../app/hooks'
import {
  GET_OPD_HISTORY_ENDPOINT,
  POST_OPD_HISTORY_ENDPOINT,
} from '../../../utils/Endpoints'
import {
  capitalize,
  FORMIK_VALIDATIONS,
  getVisitType,
  heightUnitCm,
  heightUnitFt,
  HISTORY_FIELD_KEYS,
  HISTORY_FIELD_OPT,
  HISTORY_PAIN_SCORE_MAPPING,
  weightUnitKg,
  weightUnitGm,
  HISTORY_TEXT_FIELDS,
  HISTORY_TOGGLE_FIELDS,
  typeOfValue,
  toastMessages,
} from './constants'
import {
  isHistoryFormSubmitted,
  isInitialLoadForOPDForms,
  selectHistoryExamination,
  setIsHistoryFormSubmit,
  setLoading,
  updateHistoryExamination,
} from './opdFormsSlice'

import Asterisk from '../../../components/Asterisk'
import {
  opdFormDataFetchApi,
  opdFormDataSaveApi,
} from '../../../services/OpdFormsSTTService'

const styles = (theme: Theme) => ({
  customFontBox: {
    '& .MuiTypography-root': {
      fontFamily: 'Noto Sans, sans-serif',
      color: '#363463',
    },
    '& .MuiButton-root': {
      fontFamily: 'Open Sans, sans-serif',
      fontSize: '12px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      textTransform: 'capitalize',
      width: '147px',
      margin: '2px',
      justifyContent: 'center',
      color: '#6c757d',
    },
    '& .MuiButton-contained': {
      backgroundColor: '#4682b4 !important',
      color: '#f1f3f5 !important',
      fontWeight: 600,
      border: '1px solid #4682b4 !important',
    },
    '& .MuiInputBase-input': {
      fontSize: '12px',
      width: '275px',
      padding: '4px !important',
    },
  },
  toastMessage: {
    position: 'top-right',
    className: 'small-toast',
    autoClose: 500,
    fontSize: '12px',
    padding: '6px 10px',
  },
  fieldLabel: {
    textAlign: 'right',
    textWrap: 'wrap',
    fontSize: '12px',
  },
  textFieldStyle: {
    '& .MuiInputBase-root-MuiOutlinedInput-root': {
      fontSize: '14px',
      width: '300px',
    },
  },
  textFieldWhiteBg: {
    backgroundColor: '#ffffff',
  },
  buttonStyle: {
    fontFamily: 'Open Sans, sans-serif',
    margin: '2px',
    justifyContent: 'center',
    fontSize: '12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    textTransform: 'capitalize',
    width: '147px',
  },
  selectedButton: {
    backgroundColor: '#4682b4 !important',
    color: '#f1f3f5 !important',
    fontWeight: 600,
    border: '1px solid #4682b4 !important',
  },
  unselectedButton: {
    color: '#6c757d !important',
    border: '1px solid #ddd !important',
  },
  optionButtonGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    width: '100%',
    maxWidth: '320px',
  },
  optionButton: {
    flex: '0 0 auto',
    width: '147px',
    boxSizing: 'border-box',
  },
  submitButtonContainer: {
    [theme.breakpoints.up('md')]: {
      width: '81%',
    },
    [theme.breakpoints.up('lg')]: {
      width: '79%',
    },
    [theme.breakpoints.between('sm', 'md')]: {
      width: '79%',
    },
  },
  weightContainer: {
    width: '145px !important',
  },
})

const HistoryExaminationFormTemplate = (intentType: any) => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const visitType = getVisitType(intentType.intent)
  const { id } = useParams()
  const dispatch = useDispatch<AppDispatch>()
  const historyExaminationData: any = useAppSelector(selectHistoryExamination)
  const [fhirRecordId, setFhirRecordId] = useState<string | null>(null)
  const isInitialLoadForForm = useAppSelector(isInitialLoadForOPDForms)
  const isFormSubmitted = useAppSelector(isHistoryFormSubmitted)

  const initialFormState = {
    wbps: historyExaminationData?.wbps || '',
    chiefComplaints: historyExaminationData?.chiefComplaints || '',
    historyOfPresentIllness:
      historyExaminationData?.historyOfPresentIllness || '',
    pastHistory: historyExaminationData?.pastHistory || '',
    pastMedicalHistory: historyExaminationData?.pastMedicalHistory || '',
    diet: historyExaminationData?.diet || '',
    sleep: historyExaminationData?.sleep || '',
    bowelBladder: historyExaminationData?.bowelBladder || '',
    smokingHistory: historyExaminationData?.smokingHistory || '',
    alcoholicHistory: historyExaminationData?.alcoholicHistory || '',
    habitualHistory: historyExaminationData?.habitualHistory || '',
    obstetricHistory: historyExaminationData?.obstetricHistory || '',
    familyHistory: historyExaminationData?.familyHistory || '',
    historyDrugAllergies: historyExaminationData?.historyDrugAllergies || '',
    previousSurgeries: historyExaminationData?.previousSurgeries || '',
    height: parseFloat(historyExaminationData?.height) || '',
    heightUnit: heightUnitCm,
    weight: parseFloat(historyExaminationData?.weight) || '',
    weightUnit: weightUnitKg,
    pallor: historyExaminationData?.pallor || '',
    icterus: historyExaminationData?.icterus || '',
    cyanosis: historyExaminationData?.cyanosis || '',
    clubbing: historyExaminationData?.clubbing || '',
    lymphadenopathy: historyExaminationData?.lymphadenopathy || '',
    edema: historyExaminationData?.edema || '',
  }

  const formik = useFormik({
    initialValues: {
      ...initialFormState,
    },
    validationSchema: Yup?.object({
      chiefComplaints: Yup?.string(),
      historyOfPresentIllness: Yup?.string(),
      pastHistory: Yup?.string(),
      pastMedicalHistory: Yup?.string(),
      habitualHistory: Yup?.string(),
      obstetricHistory: Yup?.string(),
      familyHistory: Yup?.string(),
      height: Yup?.number()?.required(FORMIK_VALIDATIONS.Height),
      weight: Yup?.number()?.required(FORMIK_VALIDATIONS.Weight),
    }),
    onSubmit: (values, { setSubmitting }) => {
      handleHistoryAndExamSubmitForm(values, setSubmitting)
    },
    enableReinitialize: true,
  })

  const handleHistoryAndExamSubmitForm = async (
    data: Record<string, any>,
    setSubmitting: any
  ) => {
    setSubmitting(true)

    const convertHeight = (height: number, unit: string) =>
      unit === heightUnitFt ? height * 30.48 : height
    const convertWeight = (weight: number, unit: string) =>
      unit === weightUnitGm ? weight / 1000 : weight

    const firstFourFields = HISTORY_TEXT_FIELDS?.slice(0, 4)?.map(
      (field) => field?.name
    )
    const scoreField = HISTORY_TOGGLE_FIELDS?.find(
      (field) => field?.name === HISTORY_FIELD_KEYS?.SCORE
    )
    const hurtScore = scoreField
      ? Object?.fromEntries(
          scoreField?.options?.map((opt, index) => [
            opt,
            [
              HISTORY_FIELD_OPT.nh,
              HISTORY_FIELD_OPT.hlb,
              HISTORY_FIELD_OPT.hlm,
              HISTORY_FIELD_OPT.hem,
              HISTORY_FIELD_OPT.hwl,
              HISTORY_FIELD_OPT.hw,
            ][index],
          ])
        )
      : {}

    const allFieldNames = Object?.keys(data)

    const filteredData = Object?.fromEntries(
      Object?.entries(data)
        ?.filter(([key]) => allFieldNames?.includes(key))
        ?.map(([key, value]): [string, string | number] => {
          switch (key) {
            case HISTORY_FIELD_KEYS?.SCORE:
              if (typeof value === typeOfValue && hurtScore?.[value]) {
                return [key, hurtScore?.[value]?.toLowerCase()]
              }
              return [key, value]

            case HISTORY_FIELD_KEYS?.HEIGHT:
              return [
                key,
                convertHeight(
                  Number?.(value),
                  data[HISTORY_FIELD_KEYS?.HEIGHT_UNIT]
                ),
              ]

            case HISTORY_FIELD_KEYS?.WEIGHT:
              return [
                key,
                convertWeight(
                  Number?.(value),
                  data[HISTORY_FIELD_KEYS?.WEIGHT_UNIT]
                ),
              ]

            default:
              if (!firstFourFields?.includes(key)) {
                return [
                  key,
                  typeof value === typeOfValue ? value?.toLowerCase() : value,
                ]
              }
              return [key, value]
          }
        })
    )

    const payload = {
      encounterId: id,
      visitType: `opd/${visitType}`,
      fhirRecordId: fhirRecordId,
      ...filteredData,
    }

    const response = await opdFormDataSaveApi(
      POST_OPD_HISTORY_ENDPOINT,
      payload
    )

    if (response?.success) {
      dispatch(setIsHistoryFormSubmit(true))
      toast.success(response?.message, { autoClose: 800 })
    } else {
      toast.error(response?.message || toastMessages.Form_Submission_Error, {
        autoClose: 800,
      })
    }
    setSubmitting(false)
  }

  const getHistoryExam = useCallback(async () => {
    if (!isInitialLoadForForm && !isFormSubmitted) return
    const getHistoryExamUrl = `${GET_OPD_HISTORY_ENDPOINT}${id}&visitType=opd/${visitType}`
    const response = await opdFormDataFetchApi(getHistoryExamUrl)
    if (response?.success) {
      const result = response?.data
      const fhirRecordId = response?.data?.fhirRecordId
      setFhirRecordId(fhirRecordId)

      dispatch(
        updateHistoryExamination({
          wbps: HISTORY_PAIN_SCORE_MAPPING[result?.wbps ?? ''] || '',
          chiefComplaints: result?.cheifcomp || '',
          historyOfPresentIllness: result?.hopi || '',
          pastHistory: result?.pasthis || '',
          pastMedicalHistory: result?.pastMedicalHistory || '',
          diet: capitalize(result?.diet || ''),
          sleep: capitalize(result?.sleep || ''),
          bowelBladder: capitalize(result?.bandb || ''),
          smokingHistory: capitalize(result?.smokehis || ''),
          alcoholicHistory: capitalize(result?.alchis || ''),
          habitualHistory: result?.orhis || '',
          obstetricHistory: result?.obsthis || '',
          familyHistory: result?.famhis || '',
          historyDrugAllergies: capitalize(result?.drugallergyhis || ''),
          previousSurgeries: capitalize(result?.prevsurghis || ''),
          height: result?.height || '',
          weight: result?.weight || '',
          pallor: capitalize(result?.pallor || ''),
          icterus: capitalize(result?.icterus || ''),
          cyanosis: capitalize(result?.cyanosis || ''),
          clubbing: capitalize(result?.clubbing || ''),
          lymphadenopathy: capitalize(result?.lymphadenopathy || ''),
          edema: capitalize(result?.edema || ''),
        })
      )
    } else {
      toast.error(toastMessages.Fetch_Failed)
    }
    dispatch(setLoading(false))
  }, [dispatch, id, visitType, isInitialLoadForForm, isFormSubmitted])

  useEffect(() => {
    getHistoryExam()
  }, [getHistoryExam])

  return (
    <Box
      display={'flex'}
      flexDirection={'column'}
      justifyContent={'center'}
      gap={1}
      width={'100%'}
      minWidth={'250px'}
      className={classes?.customFontBox}
    >
      {HISTORY_TOGGLE_FIELDS?.slice(0, 1)
        ?.filter((row) => row?.validVisitTypes?.includes(visitType))
        ?.map(({ name, label, options }) => (
          <Box
            display={'flex'}
            justifyContent={'start'}
            alignItems={'center'}
            width={'100%'}
            key={name}
            gap={1}
          >
            <Grid
              container
              spacing={2}
              alignItems='center'
              justifyContent={'center'}
              key={name}
            >
              <Grid item sm={4} md={4} xl={4}>
                <Typography className={classes?.fieldLabel}>
                  {label}:
                </Typography>
              </Grid>
              <Grid item sm={8} md={8} xl={8}>
                <Box
                  display='flex'
                  gap={1}
                  className={classes?.optionButtonGroup}
                >
                  {options?.map((option) => {
                    const isSelected =
                      formik?.values[name as keyof typeof formik.values] ===
                      option
                    const buttonClassName = `
            ${classes?.buttonStyle} 
            ${isSelected ? classes?.selectedButton : classes?.unselectedButton} 
            ${classes?.optionButton}
          `

                    return (
                      <Button
                        size='small'
                        key={option}
                        onClick={() => formik?.setFieldValue(name, option)}
                        className={buttonClassName}
                      >
                        {option}
                      </Button>
                    )
                  })}
                </Box>
              </Grid>
            </Grid>
          </Box>
        ))}

      {HISTORY_TEXT_FIELDS?.slice(0, 4)
        ?.filter((row) => row?.validVisitTypes?.includes(visitType))
        ?.map(({ name, label }) => (
          <Box
            display={'flex'}
            justifyContent={'start'}
            alignItems={'center'}
            width={'100%'}
            key={name}
            gap={2}
          >
            <Grid
              container
              spacing={2}
              alignItems='center'
              justifyContent={'center'}
              key={name}
            >
              <Grid item sm={4} md={4} xl={4}>
                <Typography className={classes?.fieldLabel}>
                  {label}:
                </Typography>
              </Grid>
              <Grid item sm={8} md={8} xl={8}>
                <TextField
                  inputProps={{ 'data-testid': name }}
                  multiline
                  rows={2}
                  variant={'outlined'}
                  name={name}
                  value={
                    formik?.values[name as keyof typeof formik.values] || ''
                  }
                  onChange={formik?.handleChange}
                  onBlur={formik?.handleBlur}
                  error={
                    formik?.touched[name as keyof typeof formik.values] &&
                    Boolean(formik?.errors[name as keyof typeof formik.errors])
                  }
                  className={`${classes?.textFieldStyle} ${classes?.textFieldWhiteBg}`}
                />
              </Grid>
            </Grid>
          </Box>
        ))}

      {HISTORY_TOGGLE_FIELDS?.slice(1, 6)
        ?.filter((row) => row?.validVisitTypes?.includes(visitType))
        ?.map(({ name, label, options }) => (
          <Box
            display='flex'
            justifyContent='start'
            alignItems='center'
            width='100%'
            key={name}
            flexWrap='wrap'
            gap={2}
          >
            <Grid
              container
              spacing={2}
              alignItems='center'
              justifyContent={'center'}
              key={name}
            >
              <Grid item sm={4} lg={4} xl={4}>
                <Typography className={classes?.fieldLabel}>
                  {label}:
                </Typography>
              </Grid>
              <Grid item sm={8} lg={8} xl={8}>
                <Box display='flex' flexWrap='wrap' gap={1}>
                  {options?.map((option) => {
                    const isSelected =
                      formik?.values[name as keyof typeof formik.values] ===
                      option

                    const buttonClassName = `${classes?.buttonStyle} ${
                      isSelected
                        ? classes?.selectedButton
                        : classes?.unselectedButton
                    }`

                    return (
                      <Button
                        size='small'
                        key={option}
                        onClick={() => formik?.setFieldValue(name, option)}
                        className={buttonClassName}
                      >
                        {option}
                      </Button>
                    )
                  })}
                </Box>
              </Grid>
            </Grid>
          </Box>
        ))}

      {HISTORY_TEXT_FIELDS?.slice(4, 7)
        ?.filter((row) => row?.validVisitTypes?.includes(visitType))
        ?.map(({ name, label }) => (
          <Box
            display={'flex'}
            justifyContent={'start'}
            alignItems={'center'}
            width={'100%'}
            key={name}
            flexWrap={'wrap'}
            gap={2}
          >
            <Grid
              container
              spacing={2}
              alignItems='center'
              justifyContent={'center'}
              key={name}
            >
              <Grid item sm={4} md={4} xl={4}>
                <Typography className={classes?.fieldLabel}>
                  {label}:
                </Typography>
              </Grid>
              <Grid item sm={8} md={8} xl={8}>
                <TextField
                  multiline
                  rows={2}
                  variant={'outlined'}
                  name={name}
                  value={
                    formik?.values[name as keyof typeof formik.values] || ''
                  }
                  onChange={formik?.handleChange}
                  onBlur={formik?.handleBlur}
                  error={
                    formik?.touched[name as keyof typeof formik.values] &&
                    Boolean(formik?.errors[name as keyof typeof formik.errors])
                  }
                  className={`${classes?.textFieldStyle} ${classes?.textFieldWhiteBg}`}
                />
              </Grid>
            </Grid>
          </Box>
        ))}

      {HISTORY_TOGGLE_FIELDS?.slice(6, 8)
        ?.filter((row) => row?.validVisitTypes?.includes(visitType))
        ?.map(({ name, label, options }) => (
          <Box
            display='flex'
            justifyContent='start'
            alignItems='center'
            width='100%'
            key={name}
            flexWrap='wrap'
            gap={2}
          >
            <Grid
              container
              spacing={2}
              alignItems='center'
              justifyContent={'center'}
              key={name}
            >
              <Grid item sm={4} md={4} xl={4}>
                <Typography className={classes?.fieldLabel}>
                  {label}:
                </Typography>
              </Grid>
              <Grid item sm={8} md={8} xl={8}>
                <Box display='flex' flexWrap='wrap' gap={1}>
                  {options?.map((option) => {
                    const isSelected =
                      formik?.values[name as keyof typeof formik.values] ===
                      option

                    const buttonClassName = `${classes?.buttonStyle} ${
                      isSelected
                        ? classes?.selectedButton
                        : classes?.unselectedButton
                    }`

                    return (
                      <Button
                        size='small'
                        key={option}
                        onClick={() => formik?.setFieldValue(name, option)}
                        className={buttonClassName}
                      >
                        {option}
                      </Button>
                    )
                  })}
                </Box>
              </Grid>
            </Grid>
          </Box>
        ))}
      <Box
        display='flex'
        justifyContent='start'
        alignItems='center'
        width='100%'
        flexWrap='wrap'
        gap={2}
      >
        <Grid
          container
          spacing={2}
          alignItems='center'
          justifyContent={'center'}
        >
          <Grid item sm={4} md={4} xl={4}>
            <Typography className={classes?.fieldLabel}>
              Height
              <Asterisk />:
            </Typography>
          </Grid>
          <Grid item sm={8} md={8} xl={8}>
            <TextField
              size='small'
              name='height'
              variant='outlined'
              value={formik?.values.height}
              onChange={formik?.handleChange}
              onBlur={formik?.handleBlur}
              inputProps={{ min: 1 }}
              type='number'
              error={formik?.touched?.height && Boolean(formik?.errors?.height)}
              helperText={formik?.touched?.height && formik?.errors?.height}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <Typography fontSize={'12px'}>cm</Typography>
                  </InputAdornment>
                ),
              }}
              className={`${classes?.textFieldStyle} ${classes?.textFieldWhiteBg} ${classes?.weightContainer}`}
            />
          </Grid>
        </Grid>
      </Box>

      <Box
        display='flex'
        justifyContent='start'
        alignItems='center'
        width='100%'
        flexWrap='wrap'
        gap={2}
      >
        <Grid
          container
          spacing={2}
          alignItems='center'
          justifyContent={'center'}
        >
          <Grid item sm={4} md={4} xl={4}>
            <Typography className={classes?.fieldLabel}>
              Weight
              <Asterisk />:
            </Typography>
          </Grid>
          <Grid item sm={8} md={8} xl={8}>
            <TextField
              name='weight'
              size='small'
              variant='outlined'
              value={formik?.values.weight}
              onChange={formik?.handleChange}
              onBlur={formik?.handleBlur}
              type='number'
              inputProps={{ min: 1 }}
              error={formik?.touched?.weight && Boolean(formik?.errors?.weight)}
              helperText={formik?.touched?.weight && formik?.errors?.weight}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <Typography fontSize={'12px'}>Kg</Typography>
                  </InputAdornment>
                ),
              }}
              className={`${classes?.textFieldStyle} ${classes?.textFieldWhiteBg} ${classes?.weightContainer}`}
            />
          </Grid>
        </Grid>
      </Box>
      {HISTORY_TOGGLE_FIELDS?.slice(8, 14)
        ?.filter((row) => row?.validVisitTypes?.includes(visitType))
        ?.map(({ name, label, options }) => (
          <Box
            display='flex'
            justifyContent='start'
            alignItems='center'
            width='100%'
            key={name}
            flexWrap='wrap'
            gap={2}
          >
            <Grid
              container
              spacing={2}
              alignItems='center'
              justifyContent={'center'}
              key={name}
            >
              <Grid item sm={4} md={4} xl={4}>
                <Typography className={classes?.fieldLabel}>
                  {label}:
                </Typography>
              </Grid>
              <Grid item sm={8} md={8} xl={8}>
                <Box display='flex' flexWrap='wrap' gap={1}>
                  {options?.map((option) => {
                    const isSelected =
                      formik?.values[name as keyof typeof formik.values] ===
                      option

                    const buttonClassName = `${classes?.buttonStyle} ${
                      isSelected
                        ? classes?.selectedButton
                        : classes?.unselectedButton
                    }`

                    return (
                      <Button
                        size='small'
                        key={option}
                        onClick={() => formik?.setFieldValue(name, option)}
                        className={buttonClassName}
                      >
                        {option}
                      </Button>
                    )
                  })}
                </Box>
              </Grid>
            </Grid>
          </Box>
        ))}

      <Box
        display={'flex'}
        alignItems={'center'}
        justifyContent={'flex-end'}
        className={classes?.submitButtonContainer}
      >
        <Button
          variant='contained'
          color='primary'
          onClick={formik?.submitForm}
        >
          Submit
        </Button>
      </Box>
    </Box>
  )
}

export default HistoryExaminationFormTemplate
